import { randFileName, randBetweenDate, randNumber } from '@ngneat/falso'
import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { addToDate } from '@/utils/date/calculation.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { safeMathRandom } from '@/utils/number.js'
import type { MockServer } from './server.js'

export type UploadData = {
  file: string
  original_name: string
  size: number
  created: string
  expires: string | null
  is_external: boolean
}

export const model = (Model as ModelDefinition<UploadData>).extend({
  uploader: belongsTo('user'),
  uploadGroup: belongsTo('uploadGroup'),
})

export const factory = Factory.extend({
  file() {
    const randomPath = safeMathRandom().toString(36).substring(7)
    const extensions = ['pdf', 'jpg', 'jpeg', 'png']
    const extension =
      extensions[Math.floor(safeMathRandom() * extensions.length)]
    return `https://storage-reviewapps.provetcloud.com/d1d861/provet/4/attachments/9/c/8/${randomPath}.${extension}`
  },
  original_name() {
    const extensions = ['pdf', 'jpg', 'jpeg', 'png']
    const extension =
      extensions[Math.floor(safeMathRandom() * extensions.length)]
    return `${randFileName()}.${extension}`
  },
  size() {
    return randNumber({ min: 1024, max: 102400 }) // 1KB to 100KB
  },
  created() {
    const yesterday = addToDate(getTodayDateTimeISO(), { days: -1 })
    return randBetweenDate({
      from: yesterday,
      to: new Date(),
    }).toISOString()
  },
  expires() {
    return null
  },
  is_external() {
    return safeMathRandom() > 0.5
  },
})

export function routes(server: MockServer) {
  server.post('/uploads', function (schema, request) {
    const formData = request.requestBody as unknown as FormData
    const groupIdFromRequest =
      typeof formData?.get === 'function' ? formData.get('group_id') : null

    let uploadGroup
    if (groupIdFromRequest) {
      uploadGroup = schema.find('uploadGroup', String(groupIdFromRequest))
      if (!uploadGroup) {
        uploadGroup = schema.create('uploadGroup')
      }
    } else {
      uploadGroup = schema.create('uploadGroup')
    }

    const upload = schema.create('upload', { uploadGroup })

    return {
      data: {
        type: 'FileUpload',
        id: upload.id,
        attributes: {
          filename: upload.original_name,
          original_name: upload.original_name,
          size: upload.size,
          created: upload.created,
          url: upload.file,
        },
        relationships: {
          group: {
            data: {
              type: 'UploadGroup',
              id: uploadGroup.id,
            },
          },
        },
      },
    }
  })
}

// No seeds needed for uploads as they're created dynamically
export function seeds() {
  // Uploads are created dynamically when notes are created
}
